import React, { useState, useEffect } from 'react';
import '../Component/Anniversary/AnniversarySection.css';
import { openWhatsApp, buildPackageMessage } from '../Hooks/whatsapp';
import BirthdayDataPackages from '../Hooks/BirthdaySection/BirthdaySection';
import WeddingDataPackages from '../Hooks/WeddingSection/WeddingSection';
import AnniversaryDataPackages from '../Hooks/AnniversarySection/AnniversarySection';
import { WeddingHomePageDataPackages } from '../Component/Wedding/index';
import { BirthdayHomePageDataPackages } from '../Component/Birthday/index';
import { getCategoryBySlug, getCategoryImages } from '../Hooks/categoryImages';
import './Products.css';

const Products = ({ category, categoryName, onNavigate }) => {
  const [isOpenView, setIsOpenView] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Check if category is a category image slug
  const categoryData = getCategoryBySlug(category);
  const isCategoryImage = !!categoryData;
  const categoryImages = isCategoryImage ? getCategoryImages(category) : [];

  // Get data based on category (for existing package-based categories)
  const getCategoryData = () => {
    switch (category) {
      case 'birthday':
        return BirthdayHomePageDataPackages; // Use home page data to show same products
      case 'wedding':
        return WeddingHomePageDataPackages; // Use home page data to show same products
      case 'anniversary':
        return AnniversaryDataPackages;
      default:
        return [];
    }
  };

  const getCategoryTitle = () => {
    if (categoryName) {
      return categoryName;
    }
    switch (category) {
      case 'birthday':
        return 'Birthday Decoration';
      case 'wedding':
        return 'Wedding Decoration';
      case 'anniversary':
        return 'Anniversary Decoration';
      default:
        return 'All Products';
    }
  };

  // Preload first few product images for better performance
  useEffect(() => {
    const dataPackages = getCategoryData();
    if (dataPackages && dataPackages.length > 0) {
      const imagesToPreload = dataPackages.slice(0, 6).map(pkg => pkg.image);
      imagesToPreload.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        document.head.appendChild(link);
      });
      
      return () => {
        imagesToPreload.forEach(src => {
          const links = document.querySelectorAll(`link[href="${src}"]`);
          links.forEach(link => link.remove());
        });
      };
    }
  }, [category]);

  const getImageArray = (pkg) => {
    if (pkg) {
      const images = [pkg.image];
      if (pkg.subImage && pkg.subImage[0]) {
        if (pkg.subImage[0].imageOne) images.push(pkg.subImage[0].imageOne);
        if (pkg.subImage[0].imageTwo) images.push(pkg.subImage[0].imageTwo);
        if (pkg.subImage[0].imageThree) images.push(pkg.subImage[0].imageThree);
        if (pkg.subImage[0].imageFour) images.push(pkg.subImage[0].imageFour);
      }
      return images;
    }
    return [];
  };

  // Preload next/previous images in slider when viewing
  useEffect(() => {
    if (selectedPackage && isOpenView) {
      const images = getImageArray(selectedPackage);
      if (images.length > 0) {
        const nextIndex = (currentSlide + 1) % images.length;
        const prevIndex = (currentSlide - 1 + images.length) % images.length;
        
        [images[nextIndex], images[prevIndex]].forEach(src => {
          if (src) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            document.head.appendChild(link);
          }
        });
      }
    }
  }, [currentSlide, selectedPackage, isOpenView]);

  const dataPackages = getCategoryData();
  const categoryTitle = getCategoryTitle();

  const openView = (pkg, image = null) => {
    if (pkg) {
      setSelectedPackage(pkg);
      setSelectedImage(null);
      setCurrentSlide(0);
    } else if (image) {
      setSelectedImage(image);
      setSelectedPackage(null);
      setCurrentSlide(0);
    }
    setIsOpenView(!isOpenView);
  };

  const getDisplayImages = () => {
    if (selectedPackage) {
      return getImageArray(selectedPackage);
    } else if (selectedImage !== null && categoryImages.length > 0) {
      return categoryImages;
    }
    return [];
  };

  const nextSlide = () => {
    const images = getDisplayImages();
    if (images.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }
  };

  const prevSlide = () => {
    const images = getDisplayImages();
    if (images.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleBookNow = (pkg = null) => {
    if (pkg) {
      const message = buildPackageMessage(pkg);
      openWhatsApp(message);
    } else if (categoryData) {
      // For category images, send a simple message
      const message = `Hi! I'm interested in ${categoryData.name} decoration. Please provide more details.`;
      openWhatsApp(message);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('home');
    }
  };

  return (
    <main className="products-main">
      <div className="products-header-wrapper">
        <button className="back-button" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back
        </button>
        <div className="products-header">
          <h1>{categoryTitle}</h1>
        </div>
      </div>

      {isOpenView && (selectedPackage || selectedImage !== null) ? (
        <>
          <div className="slider-overlay" onClick={openView}></div>
          <div className="product-slider-view">
            <div className="slider-view-header">
              <h2 className="slider-view-title">
                {selectedPackage ? selectedPackage.title : (categoryData ? categoryData.name : 'Gallery')}
              </h2>
              <button className="slider-close-btn" onClick={openView} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

          <div className="product-image-slider">
            <div className="slider-container-wrapper">
              <button className="slider-nav-btn slider-prev" onClick={prevSlide} aria-label="Previous">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              
              <div className="slider-images-container">
                <div 
                  className="slider-images-track" 
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {getDisplayImages().map((image, index) => (
                    <div key={index} className="slider-image-slide">
                      <img 
                        src={image} 
                        alt={`${selectedPackage ? selectedPackage.title : categoryData?.name || 'Gallery'} - Image ${index + 1}`} 
                        loading={index === 0 ? "eager" : "lazy"} 
                        decoding="async"
                        fetchPriority={index === 0 ? "high" : "auto"}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <button className="slider-nav-btn slider-next" onClick={nextSlide} aria-label="Next">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>

            {getDisplayImages().length > 1 && (
              <div className="slider-dots-container">
                {getDisplayImages().map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="slider-view-footer">
            <p className="slider-description">
              {selectedPackage ? selectedPackage.description : (categoryData ? `Explore our ${categoryData.name} decoration gallery` : '')}
            </p>
            <button className="book-now-btn-large" onClick={() => handleBookNow(selectedPackage)}>
              Book Now
            </button>
          </div>
          </div>
        </>
      ) : null}

      <div className="products-grid">
        {isCategoryImage && categoryImages.length > 0 ? (
          categoryImages.map((image, index) => (
            <div key={index} className="anniversary-card">
              <div className="card-image" onClick={() => openView(null, image)}>
                <img src={image} alt={`${categoryData.name} - Image ${index + 1}`} loading={index < 6 ? "eager" : "lazy"} decoding="async" />
                <div className="discount-badge">View</div>
              </div>
              
              <div className="card-content">
                <h3 className="card-title">{categoryData.name}</h3>
                <p className="card-description">Click to view full size</p>
                
                <button className="book-now-btn" onClick={(e) => { e.stopPropagation(); handleBookNow(); }}>Book Now</button>
              </div>
            </div>
          ))
        ) : dataPackages && dataPackages.length > 0 ? (
          dataPackages.map((pkg, index) => (
            <div key={pkg.id} className="anniversary-card">
              <div className="card-image" onClick={() => openView(pkg)}>
                <img 
                  src={pkg.image} 
                  alt={pkg.title} 
                  loading={index < 6 ? "eager" : "lazy"} 
                  decoding="async"
                  fetchPriority={index < 3 ? "high" : "auto"}
                />
                <div className="discount-badge">View</div>
              </div>
              
              <div className="card-content">
                <h3 className="card-title">{pkg.title}</h3>
                <p className="card-description">{pkg.description}</p>
                
                <button className="book-now-btn" onClick={(e) => { e.stopPropagation(); handleBookNow(pkg); }}>Book Now</button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-products">
            <p>No products found for this category.</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default Products;

